// src/Home.js

import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

import "./styles/home.css";

import {
  Wallet,
  AlertTriangle,
  CircleDollarSign,
  CreditCard,
  BellRing,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Users,
  Activity,
  Clock3,
  Receipt,
   Hand, 
} from "lucide-react";

import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  Tooltip,
} from "recharts";

const Home = () => {

  const navigate = useNavigate();

  const API_URL =
    "https://receivablesbackend.onrender.com/api";

  // ====================================================
  // STATE
  // ====================================================

  const [clients, setClients] =
    useState([]);

  const [invoices, setInvoices] =
    useState([]);

  const [payments, setPayments] =
    useState([]);

  const [pendingInvoices, setPendingInvoices] =
    useState([]);

  const [pendingPayments, setPendingPayments] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  // ====================================================
  // FETCH
  // ====================================================

  useEffect(() => {

    const fetchData = async () => {

      try {

        const [
          clientsRes,
          invoicesRes,
          paymentsRes,
          pendingSalesRes,
          pendingCreditsRes,
        ] = await Promise.all([

          fetch(`${API_URL}/customers`),

          fetch(`${API_URL}/invoices`),

          fetch(`${API_URL}/payments`),

          fetch(`${API_URL}/pending-sales`),

          fetch(`${API_URL}/pending-credits`),

        ]);

        const [
          clientsData,
          invoicesData,
          paymentsData,
          pendingSalesData,
          pendingCreditsData,
        ] = await Promise.all([

          clientsRes.json(),

          invoicesRes.json(),

          paymentsRes.json(),

          pendingSalesRes.json(),

          pendingCreditsRes.json(),

        ]);

        setClients(clientsData);

        setInvoices(invoicesData);

        setPayments(paymentsData);

        setPendingInvoices(
          pendingSalesData.filter(
            (i) =>
              i.status
                ?.toLowerCase()
                .includes("pending")
          )
        );

        setPendingPayments(
          pendingCreditsData.filter(
            (i) =>
              i.status
                ?.toLowerCase()
                .includes("pending")
          )
        );

      } catch (err) {

        console.error(err);

      } finally {

        setLoading(false);

      }
    };

    fetchData();

  }, []);

  // ====================================================
  // HELPERS
  // ====================================================

  const getInvoicePayments = (
    invoiceNumber
  ) => {

    return payments.filter(
      (p) =>
        p.invoice_number ===
        invoiceNumber
    );
  };

  const getInvoiceRemaining = (
    invoice
  ) => {

    const totalPaid =
      getInvoicePayments(
        invoice.invoice_number
      ).reduce(
        (sum, p) =>
          sum + Number(p.amount || 0),
        0
      );

    return (
      Number(invoice.amount || 0) -
      totalPaid
    );
  };

  const getInvoiceStatus = (
    invoice
  ) => {

    const remaining =
      getInvoiceRemaining(invoice);

    const dueDate =
      new Date(invoice.due_date);

    const today = new Date();

    if (remaining <= 0) {
      return "Paid";
    }

    if (dueDate < today) {
      return "Overdue";
    }

    return "Outstanding";
  };

  // ====================================================
  // KPI TOTALS
  // ====================================================

  const totalOutstanding =
    invoices.reduce((sum, inv) => {

      const remaining =
        getInvoiceRemaining(inv);

      return (
        sum +
        (remaining > 0
          ? remaining
          : 0)
      );

    }, 0);

  const totalOverdue =
    invoices
      .filter(
        (i) =>
          getInvoiceStatus(i) ===
          "Overdue"
      )
      .reduce((sum, inv) => {

        const remaining =
          getInvoiceRemaining(inv);

        return (
          sum +
          (remaining > 0
            ? remaining
            : 0)
        );

      }, 0);

  const totalPayments =
    payments.reduce(
      (sum, p) =>
        sum + Number(p.amount || 0),
      0
    );

  const totalCredit =
    clients.reduce(
      (sum, c) =>
        sum +
        Number(
          c.credit_balance || 0
        ),
      0
    );

  const collectionRate =
    totalPayments > 0
      ? (
          (totalPayments /
            (
              totalPayments +
              totalOutstanding
            )) *
          100
        ).toFixed(1)
      : 0;

  // ====================================================
  // RECENT CLIENTS
  // ====================================================

  const recentClients = useMemo(() => {

    return [...clients]

      .sort((a, b) => {

        const aDate =
          new Date(
            a.updated_at ||
            a.created_at
          );

        const bDate =
          new Date(
            b.updated_at ||
            b.created_at
          );

        return bDate - aDate;
      })

      .slice(0, 5);

  }, [clients]);

  // ====================================================
  // TOP DEBTORS
  // ====================================================

  const topDebtors = useMemo(() => {

    return clients

      .map((client) => {

        const clientInvoices =
          invoices.filter(
            (i) =>
              i.client_id ===
              client.id
          );

        const outstanding =
          clientInvoices.reduce(
            (sum, inv) =>
              sum +
              getInvoiceRemaining(
                inv
              ),
            0
          );

        return {
          ...client,
          outstanding,
        };
      })

      .sort(
        (a, b) =>
          b.outstanding -
          a.outstanding
      )

      .slice(0, 5);

  }, [clients, invoices, payments]);

  // ====================================================
  // CHART DATA
  // ====================================================

  const chartData = [

    {
      month: "Jan",
      outstanding: 400000,
      payments: 220000,
    },

    {
      month: "Feb",
      outstanding: 520000,
      payments: 320000,
    },

    {
      month: "Mar",
      outstanding: 480000,
      payments: 390000,
    },

    {
      month: "Apr",
      outstanding: 680000,
      payments: 540000,
    },

    {
      month: "May",
      outstanding: totalOutstanding,
      payments: totalPayments,
    },

  ];

  // ====================================================
  // LOADING
  // ====================================================

  if (loading) {

    return (
      <div className="dashboard-loading">

        <div className="loader-ring"></div>

      </div>
    );
  }

  // ====================================================
  // UI
  // ====================================================

  return (

    <div className="dashboard-home">

      {/* ================================================= */}
      {/* HEADER */}
      {/* ================================================= */}

      <div className="dashboard-hero">

        <div>

<h1
  style={{
    display: "flex",
    alignItems: "center",
    gap: "10px",
  }}
>
  <Hand size={28} />
  Welcome back, Admin
</h1>

          <p>
            Your receivables
            performance at a glance.
          </p>

        </div>

        <div className="hero-actions">

          <button
            onClick={() =>
              navigate("/clients")
            }
          >
            <Users size={18} />
            Clients
          </button>

          <button
            onClick={() =>
              navigate("/reports")
            }
          >
            <Receipt size={18} />
            Reports
          </button>

        </div>

      </div>

      {/* ================================================= */}
      {/* KPI GRID */}
      {/* ================================================= */}

      <div className="kpi-grid">

        <div className="kpi-card">

          <div className="kpi-icon green">

            <Wallet size={22} />

          </div>

          <span>
            Total Outstanding
          </span>

          <strong className="danger-text">

            $
            {totalOutstanding.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

          <div className="kpi-trend">

            <ArrowUpRight size={16} />

            Active balances

          </div>

        </div>

        <div className="kpi-card">

          <div className="kpi-icon red">

            <AlertTriangle size={22} />

          </div>

          <span>
            Total Overdue
          </span>

          <strong className="danger-text">

            $
            {totalOverdue.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

          <div className="kpi-trend">

            <Clock3 size={16} />

            Past due invoices

          </div>

        </div>

        <div className="kpi-card">

          <div className="kpi-icon blue">

            <CircleDollarSign size={22} />

          </div>

          <span>
            Total Payments
          </span>

          <strong className="success-text">

            $
            {totalPayments.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

          <div className="kpi-trend">

            <ArrowDownRight size={16} />

            Collections received

          </div>

        </div>

        <div className="kpi-card">

          <div className="kpi-icon teal">

            <CreditCard size={22} />

          </div>

          <span>
            Stored Credit
          </span>

          <strong>

            $
            {totalCredit.toLocaleString(
              "en-US",
              {
                minimumFractionDigits: 2,
              }
            )}

          </strong>

          <div className="kpi-trend">

            Account balances

          </div>

        </div>

        <div className="kpi-card">

          <div className="kpi-icon orange">

            <BellRing size={22} />

          </div>

          <span>
            Pending Approvals
          </span>

          <strong>

            {
              pendingInvoices.length +
              pendingPayments.length
            }

          </strong>

          <div className="approval-tags">

            <div>
              Invoices:
              {
                pendingInvoices.length
              }
            </div>

            <div>
              Payments:
              {
                pendingPayments.length
              }
            </div>

          </div>

        </div>

        <div className="kpi-card">

          <div className="kpi-icon purple">

            <TrendingUp size={22} />

          </div>

          <span>
            Collection Rate
          </span>

          <strong>

            {collectionRate}%

          </strong>

          <div className="kpi-trend">

            Recovery performance

          </div>

        </div>

      </div>

      {/* ================================================= */}
      {/* MAIN GRID */}
      {/* ================================================= */}

      <div className="dashboard-grid">

        {/* ============================================= */}
        {/* CHART */}
        {/* ============================================= */}

        <div className="dashboard-card large-card">

          <div className="card-header">

            <h3>
              Receivables Performance
            </h3>

          </div>

          <div className="chart-wrapper">

            <ResponsiveContainer
              width="100%"
              height={320}
            >

              <AreaChart
                data={chartData}
              >

                <defs>

                  <linearGradient
                    id="outstandingGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >

                    <stop
                      offset="5%"
                      stopColor="#009688"
                      stopOpacity={0.3}
                    />

                    <stop
                      offset="95%"
                      stopColor="#009688"
                      stopOpacity={0}
                    />

                  </linearGradient>

                </defs>

                <XAxis
                  dataKey="month"
                />

                <Tooltip />

                <Area
                  type="monotone"
                  dataKey="outstanding"
                  stroke="#009688"
                  fill="url(#outstandingGradient)"
                />

                <Area
                  type="monotone"
                  dataKey="payments"
                  stroke="#16a34a"
                  fillOpacity={0}
                />

              </AreaChart>

            </ResponsiveContainer>

          </div>

        </div>

        {/* ============================================= */}
        {/* TOP DEBTORS */}
        {/* ============================================= */}

        <div className="dashboard-card">

          <div className="card-header">

            <h3>
              Top Outstanding Clients
            </h3>

          </div>

          <div className="top-client-list">

            {topDebtors.map(
              (client) => (

                <div
                  key={client.id}
                  className="top-client-item"
                  onClick={() =>
                    navigate(
                      `/clients/${client.id}`
                    )
                  }
                >

                  <div className="top-client-avatar">

                    {
                      (
                        client.full_name ||
                        client.name ||
                        "C"
                      )[0]
                    }

                  </div>

                  <div className="top-client-info">

                    <strong>

                      {
                        client.full_name ||
                        client.name
                      }

                    </strong>

                    <span>

                      $
                      {Number(
                        client.outstanding
                      ).toLocaleString(
                        "en-US",
                        {
                          minimumFractionDigits: 2,
                        }
                      )}

                    </span>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

      </div>

      {/* ================================================= */}
      {/* BOTTOM GRID */}
      {/* ================================================= */}

      <div className="dashboard-grid">

        {/* ============================================= */}
        {/* RECENT CLIENTS */}
        {/* ============================================= */}

        <div className="dashboard-card">

          <div className="card-header">

            <h3>
              Recently Active Clients
            </h3>

          </div>

          <div className="activity-list">

            {recentClients.map(
              (client) => (

                <div
                  key={client.id}
                  className="activity-item"
                  onClick={() =>
                    navigate(
                      `/clients/${client.id}`
                    )
                  }
                >

                  <div className="activity-icon">

                    <Activity size={18} />

                  </div>

                  <div className="activity-content">

                    <strong>

                      {
                        client.full_name ||
                        client.name
                      }

                    </strong>

                    <span>
                      Recently updated
                    </span>

                  </div>

                </div>

              )
            )}

          </div>

        </div>

        {/* ============================================= */}
        {/* APPROVAL CENTER */}
        {/* ============================================= */}

        <div className="dashboard-card">

          <div className="card-header">

            <h3>
              Approval Center
            </h3>

          </div>

          <div className="approval-center">

            <div
              className="approval-card"
              onClick={() =>
                navigate(
                  "/approvals/invoices"
                )
              }
            >

              <Receipt size={24} />

              <div>

                <strong>
                  Invoice Requests
                </strong>

                <span>
                  {
                    pendingInvoices.length
                  } pending
                </span>

              </div>

            </div>

            <div
              className="approval-card"
              onClick={() =>
                navigate(
                  "/approvals/payments"
                )
              }
            >

              <CircleDollarSign
                size={24}
              />

              <div>

                <strong>
                  Payment Requests
                </strong>

                <span>
                  {
                    pendingPayments.length
                  } pending
                </span>

              </div>

            </div>

          </div>

        </div>

      </div>

    </div>
  );
};

export default Home;